"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { Home2, Profile2User, Calendar, FolderOpen, Briefcase } from "iconsax-react";
import { Button } from "@/components/ui/button";
import { SupportedLanguage } from "@/lib/shared";
import { TRole } from "@/actions/types";
import lang from "@/app/[locale]/(main)/lang.json";
import { localizedPath, pathWithoutLocale } from "@/lib/localePath";
import LinkPendingNotifier from "@/components/customs/navigation-pending/LinkPendingNotifier";
import { useNavigationPending } from "@/components/customs/navigation-pending/NavigationPendingContext";

type SidebarItem = {
  titleKey: keyof typeof lang;
  href: string;
  icon: React.ReactNode;
};

type MainSidebarProps = {
  locale: SupportedLanguage;
  role?: TRole;
};

export const sidebarItems: (pathAfterLocale: string) => SidebarItem[] = (
  pathAfterLocale: string,
) => [
  {
    titleKey: "sidebar-home",
    href: "/",
    icon: (
      <Home2
        className={pathAfterLocale === "/" ? "fill-current" : "stroke-current"}
        size={24}
        variant={pathAfterLocale === "/" ? "Bold" : "Linear"}
      />
    ),
  },
  {
    titleKey: "sidebar-following",
    href: "/following",
    icon: (
      <Profile2User
        className={
          pathAfterLocale === "/following" ||
          pathAfterLocale.startsWith("/following/")
            ? "fill-current"
            : "stroke-current"
        }
        size={24}
        variant={
          pathAfterLocale === "/following" ||
          pathAfterLocale.startsWith("/following/")
            ? "Bold"
            : "Linear"
        }
      />
    ),
  },
  {
    titleKey: "sidebar-schedule",
    href: "/schedules",
    icon: (
      <Calendar
        className={
          pathAfterLocale === "/schedules" ||
          pathAfterLocale.startsWith("/schedules/")
            ? "fill-current"
            : "stroke-current"
        }
        size={24}
        variant={
          pathAfterLocale === "/schedules" ||
          pathAfterLocale.startsWith("/schedules/")
            ? "Bold"
            : "Linear"
        }
      />
    ),
  },
  {
    titleKey: "sidebar-comparison",
    href: "/comparison",
    icon: (
      <FolderOpen
        className={
          pathAfterLocale === "/comparison" ||
          pathAfterLocale.startsWith("/comparison/")
            ? "fill-current"
            : "stroke-current"
        }
        size={24}
        variant={
          pathAfterLocale === "/comparison" ||
          pathAfterLocale.startsWith("/comparison/")
            ? "Bold"
            : "Linear"
        }
      />
    ),
  },
];

const jobsSidebarItem = (pathAfterLocale: string): SidebarItem => ({
  titleKey: "sidebar-jobs",
  href: "/settings/jobs",
  icon: (
    <Briefcase
      className={
        pathAfterLocale.includes("/settings/jobs")
          ? "fill-current"
          : "stroke-current"
      }
      size={24}
      variant={pathAfterLocale.includes("/settings/jobs") ? "Bold" : "Linear"}
    />
  ),
});

/** True if the real URL is already this item (do not re-fire navigation intent). */
export function isAlreadyOnRoute(pathAfterLocale: string, itemHref: string): boolean {
  if (itemHref === "/") {
    return pathAfterLocale === "/" || pathAfterLocale === "";
  }
  if (itemHref === "/settings/jobs") {
    return pathAfterLocale.startsWith("/settings/jobs");
  }
  return (
    pathAfterLocale === itemHref ||
    pathAfterLocale.startsWith(`${itemHref}/`)
  );
}

export function isSidebarItemActive(effectivePath: string, itemHref: string): boolean {
  if (itemHref === "/") {
    return effectivePath === "/" || effectivePath === "";
  }
  if (itemHref === "/settings/jobs") {
    return effectivePath.startsWith("/settings/jobs");
  }
  const pathSegments = effectivePath.split("/").filter(Boolean);
  const itemPath = itemHref.substring(1);
  return (
    pathSegments.some((segment) => segment === itemPath) ||
    (pathSegments.length > 0 && pathSegments[0] === itemPath)
  );
}

export default function MainSidebar({ locale, role }: MainSidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { navigationTarget, setPendingFromLink } = useNavigationPending();

  const pathAfterLocale = React.useMemo(
    () => pathWithoutLocale(pathname),
    [pathname],
  );

  /** Optimistic path for labels/icons/active — switches the instant you press the link */
  const effectivePath = navigationTarget ?? pathAfterLocale;

  const hasItsOwnSidebar = React.useMemo(() => {
    const pathNoLocale = pathAfterLocale;

    if (pathNoLocale === "/privacy") return true;
    if (pathNoLocale === "/help") return true;
    if (pathNoLocale.startsWith("/settings")) return true;
    if (pathNoLocale.startsWith("/me/") && pathNoLocale !== "/me")
      return true;
    if (/^\/profiles\/[^/]+\/.+$/.test(pathNoLocale)) return true;

    return false;
  }, [pathAfterLocale]);

  const prefetchTargets = React.useMemo(() => {
    const items = sidebarItems("/").map((item) =>
      localizedPath(locale, item.href),
    );
    if (role === "Company") {
      items.push(localizedPath(locale, "/settings/jobs"));
    }
    return items;
  }, [locale, role]);

  React.useEffect(() => {
    if (hasItsOwnSidebar) return;
    prefetchTargets.forEach((href) => {
      try {
        router.prefetch(href);
      } catch {
        /* ignore */
      }
    });
  }, [router, prefetchTargets, hasItsOwnSidebar]);

  if (hasItsOwnSidebar) {
    return null;
  }

  return (
    <aside
      className="hidden xl:flex flex-col w-60 border-r rtl:border-r-0 rtl:border-l bg-white sticky overflow-y-auto h-[calc(100vh-65px)] lg:h-[calc(100vh-104px)] top-[65px] lg:top-[104px]"
    >
      <div className="flex flex-col space-y-2 p-4">
        {[
          ...sidebarItems(effectivePath),
          ...(role === "Company" ? [jobsSidebarItem(effectivePath)] : []),
        ].map((item) => {
          const href = localizedPath(locale, item.href);
          const isActive = isSidebarItemActive(effectivePath, item.href);

          return (
            <Button
              key={item.href}
              variant="ghost"
              className={cn(
                "justify-start gap-3 h-12 font-bold w-full transition-colors duration-0",
                isActive ? "text-primary" : "text-gray-500",
              )}
              asChild
            >
              <Link
                href={href}
                prefetch
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  if (isAlreadyOnRoute(pathAfterLocale, item.href)) return;
                  setPendingFromLink(item.href, true);
                }}
                onPointerEnter={() => {
                  try {
                    router.prefetch(href);
                  } catch {
                    /* ignore */
                  }
                }}
              >
                <LinkPendingNotifier pathAfterLocale={item.href} />
                {item.icon}
                <span className="font-medium">
                  {lang[item.titleKey][locale]}
                </span>
              </Link>
            </Button>
          );
        })}
      </div>
    </aside>
  );
}
